import Auth from '@aws-amplify/auth';
import { SignInOpts } from '@aws-amplify/auth/lib-esm/types';
import { AbstractApiRequestService } from '@coach-bot/data-access/shared/services/abstract/abstract-api-request.service';
import { ApiRequestType } from '@shared/extension/enums/api-request-type.enum';
import {
  ApiRequest,
  ApiResponse,
  FetchRequestData,
  GraphQLRequestData,
} from '@shared/extension/interfaces/api-request.interface';
import { Observable, Subject, Subscription, firstValueFrom } from 'rxjs';
import { Services } from './services.service';

export class ApiRequestService extends AbstractApiRequestService {
  private apiResponsesMapUpdated$ = new Subject<void>();

  private apiResponsesMap: Map<string, ApiResponse> = new Map();
  private pendingApiRequests: Map<string, ApiRequest> = new Map();

  constructor(private services: Services) {
    super();
  }

  async getApiRequestData<T = unknown>(request: ApiRequest<unknown>, cacheResponse?: boolean): Promise<T> {
    return this.sendApiRequest<T>(request, cacheResponse).then(response => response.data);
  }

  async getAppSyncQueryData<T = unknown>(
    query: string,
    variables?: { [key: string]: unknown },
    cacheResponse?: boolean
  ): Promise<T> {
    return this.sendAppSyncQuery<T>(query, variables, cacheResponse).then(response => response.data);
  }

  async sendApiRequest<T = unknown, E = unknown>(
    request: ApiRequest,
    cacheResponse?: boolean
  ): Promise<ApiResponse<T, E>> {
    const cacheKey: string = this.getCacheKey(request);
    if (this.apiResponsesMap.has(cacheKey)) {
      return this.apiResponsesMap.get(cacheKey) as ApiResponse<T, E>;
    }
    if (this.pendingApiRequests.has(cacheKey)) {
      return new Promise<ApiResponse<T, E>>(resolve => {
        const subscription: Subscription = this.apiResponsesMapUpdated$.subscribe(() => {
          if (this.apiResponsesMap.has(cacheKey)) {
            subscription.unsubscribe();
            resolve(this.apiResponsesMap.get(cacheKey) as ApiResponse<T, E>);
          }
        });
      });
    }
    if (cacheResponse) {
      this.pendingApiRequests.set(cacheKey, request);
    }
    const response: ApiResponse<T, E> = { success: true, time: Date.now() };
    try {
      await this.handleRequestTypes<T, E>(request, response);
    } catch (ex: unknown) {
      if (!response.data) {
        response.error = ex as E;
        response.success = false;
      }
    }
    response.time = Date.now();
    if (cacheResponse) {
      this.pendingApiRequests.delete(cacheKey);
      this.apiResponsesMap.set(cacheKey, response);
      this.apiResponsesMapUpdated$.next();
    }
    return response;
  }

  async sendAppSyncQuery<T = unknown, E = unknown>(
    query: string,
    variables?: { [key: string]: unknown },
    cacheResponse?: boolean
  ): Promise<ApiResponse<T, E>> {
    return this.sendApiRequest<T, E>(
      { type: ApiRequestType.AppSyncQuery, data: { queryOrMutation: query, variables } as GraphQLRequestData },
      cacheResponse
    );
  }

  subscribe<T = unknown>(query: string, variables?: { [key: string]: unknown }): Observable<T> {
    return this.services.appSyncService.subscribe<T, { [key: string]: unknown }>(query, variables);
  }

  clearApiRequestCache(request: ApiRequest): void {
    this.apiResponsesMap.delete(this.getCacheKey(request));
  }

  private async handleRequestTypes<T, E>(request: ApiRequest, response: ApiResponse<T, E>): Promise<void> {
    switch (request.type) {
      case ApiRequestType.AppSyncMutation: {
        response.data = await this.handleAppSyncRequest(
          request.data as GraphQLRequestData,
          this.services.appSyncService.mutate
        );
        break;
      }
      case ApiRequestType.AppSyncMutationWithApiKey: {
        response.data = await this.handleAppSyncRequest(
          request.data as GraphQLRequestData,
          this.services.appSyncService.mutateViaApiKeyAuth
        );
        break;
      }
      case ApiRequestType.AppSyncQuery: {
        response.data = await this.handleAppSyncRequest(
          request.data as GraphQLRequestData,
          this.services.appSyncService.query
        );
        break;
      }
      case ApiRequestType.AppSyncQueryWithApiKey: {
        response.data = await this.handleAppSyncRequest(
          request.data as GraphQLRequestData,
          this.services.appSyncService.queryViaApiKeyAuth
        );
        break;
      }
      case ApiRequestType.Fetch: {
        const data = request.data as FetchRequestData;
        const res: Response = await fetch(data.url, data.params);
        if (res.ok) {
          response.data = await res.json();
        } else {
          const errorBody = await res.json();
          throw new Error(`Fetch error: ${res.status} ${res.statusText} - ${JSON.stringify(errorBody)}`);
        }
        break;
      }
      case ApiRequestType.ForgotPassword: {
        await Auth.forgotPassword(request.data as string);
        break;
      }
      case ApiRequestType.SignIn: {
        response.data = (await this.services.authService.signIn(request.data as SignInOpts)) as T;
        break;
      }
      case ApiRequestType.SignOut: {
        await this.services.authService.signOut();
        break;
      }
      default: {
        throw new Error('Unknown API request type');
      }
    }
  }

  private getCacheKey(request: ApiRequest): string {
    return JSON.stringify(request);
  }

  private async handleAppSyncRequest(requestData: GraphQLRequestData, appSyncMethod: Function): Promise<any> {
    return await firstValueFrom(appSyncMethod(requestData.queryOrMutation, requestData.variables));
  }
}
